import type { JobTypeVersion } from '../api/client';
import './HistoryTab.css';

interface HistoryTabProps {
  versions: JobTypeVersion[];
  onView: (v: JobTypeVersion) => void;
  onRestore: (v: JobTypeVersion) => void;
  onCherryPick: (v: JobTypeVersion) => void;
}

function summarizeChangelog(changelog: Record<string, unknown>): string {
  const diff = changelog.diff as { fieldsAdded?: string[]; fieldsRemoved?: string[]; fieldsChanged?: Array<{ name: string }> } | undefined;
  if (!diff) return (changelog.note as string | undefined) ?? '—';
  const parts: string[] = [];
  if (diff.fieldsAdded?.length) parts.push(`${diff.fieldsAdded.length} added`);
  if (diff.fieldsRemoved?.length) parts.push(`${diff.fieldsRemoved.length} removed`);
  if (diff.fieldsChanged?.length) parts.push(`${diff.fieldsChanged.length} changed`);
  return parts.join(', ') || '—';
}

export function HistoryTab({ versions, onView, onRestore, onCherryPick }: HistoryTabProps) {
  return (
    <table className="history-tab">
      <thead>
        <tr>
          <th>#</th><th>State</th><th>Created by</th><th>Published by</th><th>Created / Published</th><th>Summary</th><th>Note</th><th>Actions</th>
        </tr>
      </thead>
      <tbody>
        {versions.map((v) => {
          const isDraft = v.state === 'draft';
          const draftTitle = 'This is your current draft';
          return (
            <tr key={v.id}>
              <td>v{v.versionNumber}</td>
              <td><span className={`state-badge state-badge--${v.state}`}>{v.state}</span></td>
              <td>{v.user?.name ?? v.createdBy ?? '—'}</td>
              <td>{v.publishedByUser?.name ?? v.publishedBy ?? '—'}</td>
              <td>{(v.publishedAt ?? v.createdAt).slice(0, 10)}</td>
              <td>{summarizeChangelog(v.changelog)}</td>
              <td className="history-tab__note">{v.publishNote ?? '—'}</td>
              <td>
                <button type="button" onClick={() => onView(v)}>View</button>
                <button
                  type="button"
                  onClick={() => onRestore(v)}
                  disabled={isDraft}
                  title={isDraft ? draftTitle : undefined}
                  aria-disabled={isDraft || undefined}
                >
                  Restore
                </button>
                <button
                  type="button"
                  onClick={() => onCherryPick(v)}
                  disabled={isDraft}
                  title={isDraft ? draftTitle : undefined}
                  aria-disabled={isDraft || undefined}
                >
                  Cherry-pick
                </button>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}
